import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, Funnel, MagnifyingGlass, Coins, X as XIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const INCOME_CATEGORIES = [
  "Sosyal Medya Yönetimi",
  "Reklam Kampanyası",
  "Tasarım Hizmetleri",
  "Danışmanlık",
  "Diğer",
];

const EXPENSE_CATEGORIES = [
  "Ofis Giderleri",
  "Personel Giderleri",
  "Reklam/Pazarlama Giderleri",
  "Teknoloji/Yazılım",
  "Diğer",
];

/**
 * Shared transactions UI used by both Income and Expenses pages.
 * Props:
 *  - kind: 'gelir' | 'gider'
 *  - title: page title
 *  - subtitle: page subtitle
 */
const TransactionsView = ({ kind, title, subtitle, testIdPrefix }) => {
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState({ count: 0, ciro: 0, tahsil: 0, bekleyen: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });
  const [filters, setFilters] = useState({
    category: "",
    payment_status: "",
    month: "",
  });
  const [formData, setFormData] = useState({
    customer_id: "",
    category: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    payment_status: "ödenmedi",
    invoice_status: "faturasız",
    description: "",
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const categories = kind === "gelir" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("transaction_type", kind);
      if (filters.category) params.append("category", filters.category);
      if (filters.payment_status) params.append("payment_status", filters.payment_status);

      // İSTEK 1: tablo verisi (filtre + ay olmaksızın — chip ay seçimi listeyi sınırlamalı ama backend stats ile uyumlu)
      const tablePromise = axios.get(`${API_URL}/api/transactions?${params}`, { withCredentials: true });
      const customersPromise = axios.get(`${API_URL}/api/customers`, { withCredentials: true });

      // İSTEK 2: özet kartlar için backend hesabı — kullanıcı bir ay seçtiyse o ayı dahil et
      const statsParams = new URLSearchParams(params);
      if (filters.month) statsParams.append("month", filters.month);
      const statsPromise = axios.get(`${API_URL}/api/transactions/stats?${statsParams}`, { withCredentials: true });

      const [tRes, cRes, sRes] = await Promise.all([tablePromise, customersPromise, statsPromise]);
      setTransactions(tRes.data);
      setCustomers(cRes.data);
      setSummaryStats(sRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, [API_URL, kind, filters.category, filters.payment_status, filters.month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        transaction_type: kind,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
        customer_id: formData.customer_id || null,
      };

      if (editing) {
        await axios.put(`${API_URL}/api/transactions/${editing.id}`, payload, { withCredentials: true });
        toast.success("İşlem güncellendi");
      } else {
        await axios.post(`${API_URL}/api/transactions`, payload, { withCredentials: true });
        toast.success("İşlem eklendi");
      }
      fetchData();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error("Kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu işlemi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/transactions/${id}`, { withCredentials: true });
      toast.success("İşlem silindi");
      fetchData();
    } catch (error) {
      toast.error("Silinirken hata oluştu");
    }
  };

  const handleEdit = (t) => {
    setEditing(t);
    setFormData({
      customer_id: t.customer_id || "",
      category: t.category,
      amount: t.amount.toString(),
      date: new Date(t.date).toISOString().split("T")[0],
      payment_status: t.payment_status,
      invoice_status: t.invoice_status,
      description: t.description || "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      customer_id: "",
      category: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      payment_status: "ödenmedi",
      invoice_status: "faturasız",
      description: "",
    });
  };

  // Payments (kısmi ödeme) handlers
  const openPaymentModal = async (t) => {
    setPaymentTarget(t);
    setShowPaymentModal(true);
    setPaymentForm({
      amount: (t.remaining || (t.amount - (t.total_paid || 0))).toString(),
      date: new Date().toISOString().split("T")[0],
      notes: "",
    });
    try {
      const { data } = await axios.get(`${API_URL}/api/transactions/${t.id}/payments`, { withCredentials: true });
      setPaymentsList(data);
    } catch (error) {
      console.error(error);
      setPaymentsList([]);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentTarget(null);
    setPaymentsList([]);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error("Geçerli bir tutar girin");
      return;
    }
    try {
      const { data: payment } = await axios.post(
        `${API_URL}/api/transactions/${paymentTarget.id}/payments`,
        {
          amount,
          date: new Date(paymentForm.date).toISOString(),
          notes: paymentForm.notes || null,
        },
        { withCredentials: true }
      );
      toast.success(`₺${amount.toLocaleString("tr-TR")} ödeme eklendi`);
      // Refresh payments list + main list
      const { data: newPayments } = await axios.get(`${API_URL}/api/transactions/${paymentTarget.id}/payments`, { withCredentials: true });
      setPaymentsList(newPayments);
      // Update the target with new values (refetch transactions)
      await fetchData();
      // Update local paymentTarget for display
      const refreshed = transactions.find((x) => x.id === paymentTarget.id);
      if (refreshed) setPaymentTarget(refreshed);
      setPaymentForm({ amount: "", date: new Date().toISOString().split("T")[0], notes: "" });
    } catch (error) {
      console.error(error);
      toast.error("Ödeme eklenemedi");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Bu ödeme kaydını silmek istiyor musunuz?")) return;
    try {
      await axios.delete(`${API_URL}/api/transactions/${paymentTarget.id}/payments/${paymentId}`, { withCredentials: true });
      toast.success("Ödeme silindi");
      const { data: newPayments } = await axios.get(`${API_URL}/api/transactions/${paymentTarget.id}/payments`, { withCredentials: true });
      setPaymentsList(newPayments);
      await fetchData();
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  // Filter search client-side + month filter
  const filtered = transactions.filter((t) => {
    // Month filter (YYYY-MM)
    if (filters.month) {
      const d = new Date(t.date);
      const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (mk !== filters.month) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (t.customer_name || "").toLowerCase().includes(s) ||
      (t.category || "").toLowerCase().includes(s) ||
      (t.description || "").toLowerCase().includes(s)
    );
  });

  // Compute available months from ALL transactions (not filtered)
  const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  const availableMonths = (() => {
    const setM = new Set();
    transactions.forEach((t) => {
      const d = new Date(t.date);
      setM.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    });
    return Array.from(setM).sort();
  })();
  const monthLabel = (mk) => {
    const [y, m] = mk.split("-");
    return `${TR_MONTHS[parseInt(m, 10) - 1]} ${y.slice(-2)}`;
  };

  // Aylık Ciro / Tahsil / Bekleyen — BACKEND'DEN gelir (Dashboard ile birebir aynı kaynak)
  const monthlyCiro = summaryStats.ciro || 0;
  const totalPaid = summaryStats.tahsil || 0;
  const totalPending = summaryStats.bekleyen || 0;
  const summaryCount = summaryStats.count || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div>
      </div>
    );
  }

  const isIncome = kind === "gelir";

  return (
    <div className="p-8" data-testid={`${testIdPrefix}-page`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">{title}</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">{subtitle}</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid={`add-${testIdPrefix}-button`}
          className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-sm shadow-premium hover:opacity-90 premium-hover"
        >
          <Plus size={18} weight="bold" />
          Yeni {isIncome ? "Gelir" : "Gider"}
        </button>
      </div>

      {/* Ay-Ay seçici (chip row) */}
      {availableMonths.length > 0 && (
        <div className="mb-6" data-testid={`${testIdPrefix}-month-chips`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))]">Ay Seçimi</span>
            {filters.month && (
              <button
                onClick={() => setFilters({ ...filters, month: "" })}
                data-testid={`${testIdPrefix}-clear-month`}
                className="text-[10px] text-[hsl(var(--text-faint))] hover:text-[hsl(var(--text-strong))] underline"
              >
                Temizle
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
            <button
              onClick={() => setFilters({ ...filters, month: "" })}
              data-testid={`${testIdPrefix}-month-chip-all`}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border ${
                !filters.month
                  ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))] shadow-premium-sm"
                  : "bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-soft))] border-[hsl(var(--border))] hover:border-[hsl(var(--text-strong))] hover:text-[hsl(var(--text-strong))]"
              }`}
            >
              Tümü
            </button>
            {availableMonths.map((mk) => (
              <button
                key={mk}
                onClick={() => setFilters({ ...filters, month: mk })}
                data-testid={`${testIdPrefix}-month-chip-${mk}`}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border ${
                  filters.month === mk
                    ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))] shadow-premium-sm"
                    : "bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-soft))] border-[hsl(var(--border))] hover:border-[hsl(var(--text-strong))] hover:text-[hsl(var(--text-strong))]"
                }`}
              >
                {monthLabel(mk)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-6 rounded-2xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-premium" data-testid={`${testIdPrefix}-monthly-ciro-card`}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70 mb-2">
            {filters.month ? `${monthLabel(filters.month)} Cirosu` : "Toplam Ciro"}
          </p>
          <p className="text-3xl font-display font-bold tabular-nums">₺{monthlyCiro.toLocaleString("tr-TR")}</p>
          <p className="text-[10px] opacity-60 mt-1">{summaryCount} işlem · faturalı/faturasız tüm tutarlar</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Toplam Kayıt</p>
          <p className="text-3xl font-display font-bold text-[hsl(var(--text-strong))] tabular-nums">{summaryCount}</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">{isIncome ? "Tahsil Edilen" : "Ödenen"}</p>
          <p className="text-3xl font-display font-bold text-[hsl(var(--success))] tabular-nums">₺{totalPaid.toLocaleString("tr-TR")}</p>
        </div>
        <div className="p-6 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] shadow-premium-sm">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">{isIncome ? "Bekleyen" : "Ödenmeyen"}</p>
          <p className="text-3xl font-display font-bold text-[hsl(var(--warning))] tabular-nums">₺{totalPending.toLocaleString("tr-TR")}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="p-5 rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] mb-6 shadow-premium-sm">
        <div className="flex items-center gap-2 mb-4">
          <Funnel size={16} weight="duotone" className="text-[hsl(var(--text-soft))]" />
          <h3 className="text-sm font-semibold text-[hsl(var(--text-strong))]">Filtreler</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-faint))]" weight="bold" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Müşteri, kategori ara..."
              data-testid={`${testIdPrefix}-search-input`}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] focus:border-[hsl(var(--text-strong))] focus:outline-none"
            />
          </div>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            data-testid={`${testIdPrefix}-filter-category`}
            className="px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] focus:border-[hsl(var(--text-strong))] focus:outline-none"
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={filters.payment_status}
            onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
            data-testid={`${testIdPrefix}-filter-status`}
            className="px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] focus:border-[hsl(var(--text-strong))] focus:outline-none"
          >
            <option value="">Tüm Durumlar</option>
            <option value="ödendi">Ödendi</option>
            <option value="kısmi">Kısmi</option>
            <option value="ödenmedi">Ödenmedi</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tarih</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">{isIncome ? "Müşteri" : "Açıklama"}</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Kategori</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tutar</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Ödeme</th>
                <th className="text-center px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Fatura</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-[hsl(var(--text-faint))]">Kayıt bulunamadı</td></tr>
              ) : filtered.map((t, idx) => {
                const statusColor =
                  t.payment_status === "ödendi" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
                  : t.payment_status === "kısmi" ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]"
                  : "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]";
                const totalPaidVal = t.total_paid || 0;
                const remainingVal = t.remaining ?? (t.amount - totalPaidVal);
                return (
                <tr key={t.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40" data-testid={`${testIdPrefix}-row-${idx}`}>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-strong))] tabular-nums">{new Date(t.date).toLocaleDateString("tr-TR")}</td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-strong))] font-medium">{t.customer_name || t.description || "-"}</td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">{t.category}</td>
                  <td className="px-5 py-3.5 text-sm text-right tabular-nums">
                    <div className="font-semibold text-[hsl(var(--text-strong))]">₺{t.amount.toLocaleString("tr-TR")}</div>
                    {t.payment_status === "kısmi" && (
                      <div className="text-[10px] text-[hsl(var(--text-faint))] mt-0.5">
                        Ödenen: ₺{totalPaidVal.toLocaleString("tr-TR")} · Kalan: ₺{remainingVal.toLocaleString("tr-TR")}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-block px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-md ${statusColor}`}>
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center text-xs text-[hsl(var(--text-soft))]">{t.invoice_status}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1">
                      {isIncome && t.payment_status !== "ödendi" && (
                        <button onClick={() => openPaymentModal(t)} data-testid={`add-payment-${testIdPrefix}-${idx}`} title="Kısmi ödeme ekle" className="p-2 rounded-lg hover:bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--accent-gold))]">
                          <Coins size={14} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => handleEdit(t)} data-testid={`edit-${testIdPrefix}-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]">
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} data-testid={`delete-${testIdPrefix}-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--danger))]">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-md" data-testid={`${testIdPrefix}-modal`}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">
              {editing ? "İşlemi Düzenle" : `Yeni ${isIncome ? "Gelir" : "Gider"} Ekle`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
            {isIncome && (
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Müşteri</label>
                <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} data-testid={`${testIdPrefix}-customer-select`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                  <option value="">Seçiniz (opsiyonel)</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Kategori *</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required data-testid={`${testIdPrefix}-category-select`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Seçiniz</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Tutar *</label>
                <input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required data-testid={`${testIdPrefix}-amount-input`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Tarih *</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid={`${testIdPrefix}-date-input`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Ödeme</label>
                <select value={formData.payment_status} onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })} data-testid={`${testIdPrefix}-payment-select`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                  <option value="ödendi">Ödendi</option>
                  <option value="ödenmedi">Ödenmedi</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Fatura</label>
                <select value={formData.invoice_status} onChange={(e) => setFormData({ ...formData, invoice_status: e.target.value })} data-testid={`${testIdPrefix}-invoice-select`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                  <option value="faturalı">Faturalı</option>
                  <option value="faturasız">Faturasız</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Açıklama</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} data-testid={`${testIdPrefix}-description-input`} className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>

            <div className="flex gap-2 pt-3">
              <button type="button" onClick={handleClose} data-testid={`cancel-${testIdPrefix}-button`} className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">İptal</button>
              <button type="submit" data-testid={`save-${testIdPrefix}-button`} className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Kaydet</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment (Kısmi Ödeme) Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(o) => !o && closePaymentModal()}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-lg" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">Kısmi Ödeme</DialogTitle>
          </DialogHeader>

          {paymentTarget && (
            <div className="space-y-5">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 p-4 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--text-soft))] mb-1">Müşteri</p>
                  <p className="text-sm font-semibold text-[hsl(var(--text-strong))]">{paymentTarget.customer_name || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--text-soft))] mb-1">Toplam Tutar</p>
                  <p className="text-sm font-bold text-[hsl(var(--text-strong))] tabular-nums">₺{paymentTarget.amount.toLocaleString("tr-TR")}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--text-soft))] mb-1">Kalan</p>
                  <p className="text-sm font-bold text-[hsl(var(--accent-gold))] tabular-nums">
                    ₺{(paymentTarget.remaining ?? paymentTarget.amount - (paymentTarget.total_paid || 0)).toLocaleString("tr-TR")}
                  </p>
                </div>
              </div>

              {/* Existing Payments */}
              {paymentsList.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Önceki Ödemeler ({paymentsList.length})</p>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-[hsl(var(--border))]">
                    {paymentsList.map((p, idx) => (
                      <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-[hsl(var(--border))] last:border-b-0 text-sm" data-testid={`payment-item-${idx}`}>
                        <div>
                          <span className="font-semibold text-[hsl(var(--text-strong))] tabular-nums">₺{p.amount.toLocaleString("tr-TR")}</span>
                          <span className="text-xs text-[hsl(var(--text-faint))] ml-2">{new Date(p.date).toLocaleDateString("tr-TR")}</span>
                          {p.notes && <p className="text-[10px] text-[hsl(var(--text-faint))] mt-0.5">{p.notes}</p>}
                        </div>
                        <button onClick={() => handleDeletePayment(p.id)} data-testid={`delete-payment-${idx}`} className="p-1.5 rounded hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-faint))] hover:text-[hsl(var(--danger))]">
                          <XIcon size={14} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Form */}
              <form onSubmit={handleAddPayment} className="space-y-3 pt-2 border-t border-[hsl(var(--border))]" data-testid="add-payment-form">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))]">Yeni Ödeme Ekle</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-1.5">Tutar *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      required
                      data-testid="payment-amount-input"
                      className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))] tabular-nums"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-1.5">Tarih *</label>
                    <input
                      type="date"
                      value={paymentForm.date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                      required
                      data-testid="payment-date-input"
                      className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-1.5">Not</label>
                  <input
                    type="text"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Örn: Taksit 1/3"
                    data-testid="payment-notes-input"
                    className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]"
                  />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={closePaymentModal} data-testid="close-payment-modal-button" className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">Kapat</button>
                  <button type="submit" data-testid="save-payment-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Ödemeyi Kaydet</button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsView;
